import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import CardListItem from '../components/CardListItem';
import CategoryFilter from '../components/CategoryFilter';
import RangeFilter from '../components/RangeFilter';
import { debounce } from 'lodash';
import analyticsService from '../services/analyticsService';
import { useAuth } from '../contexts/AuthContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { collectionAPI, wishlistAPI } from '../lib/api';
import { roundPriceAmount } from '../lib/pricing';
import PageMeta from '../components/PageMeta';
import SealedProductsSection from '../components/SealedProductsSection';
import { ProductGrid } from '../components/ProductTile';
import GameTabs, { PillTabs } from '../components/GameTabs';
import { useSearchGame, DEFAULT_GAME, ALL_GAMES, searchTcgParam, fetchSearchJson, showGameBadges, gameOfId } from '../lib/games';
import { SEALED_QUERY, groupsFromFacets, groupByKey } from '../lib/productTypes';

function analyzeCardData(cards) {
  if (!Array.isArray(cards) || cards.length === 0) {
    // Return empty structure instead of error to prevent frontend crashes
    return {
      unique: {
        seriesName: [],
        setName: [],
        types: [],
        rarity: []
      },
      numerical: {
        hp: { min: null, max: null },
        price: { min: null, max: null }
      }
    };
  }

  // Initialize result object
  const result = {
    unique: {
      seriesName: new Set(),
      setName: new Set(),
      types: new Set(),
      rarity: new Set()
    },
    numerical: {
      hp: { min: Infinity, max: -Infinity },
      price: { min: Infinity, max: -Infinity }
    }
  };

  // Process each card
  cards.forEach(card => {
    // Extract unique categorical values
    if (card.seriesName) result.unique.seriesName.add(card.seriesName.toLowerCase());
    if (card.setName) result.unique.setName.add(card.setName.toLowerCase());
    if (card.rarity) result.unique.rarity.add(card.rarity.toLowerCase());
    
    // Process types array
    if (Array.isArray(card.types)) {
      card.types.forEach(type => {
        if (type) result.unique.types.add(type.toLowerCase());
      });
    }
    
    // Process numerical HP
    if (card.hp) {
      const hpValue = parseInt(card.hp, 10);
      if (!isNaN(hpValue)) {
        result.numerical.hp.min = Math.min(result.numerical.hp.min, hpValue);
        result.numerical.hp.max = Math.max(result.numerical.hp.max, hpValue);
      }
    }
    
    // Process pricing - use new consolidated pricing system
    // Try to get primary price first (from new API)
    if (card.primaryPrice && card.primaryPrice > 0) {
      result.numerical.price.min = Math.min(result.numerical.price.min, card.primaryPrice);
      result.numerical.price.max = Math.max(result.numerical.price.max, card.primaryPrice);
    }
    // Fallback to static variants for backward compatibility
    else if (card.variants) {
      Object.values(card.variants).forEach(variant => {
        if (variant && variant.price !== undefined && variant.price > 0) {
          result.numerical.price.min = Math.min(result.numerical.price.min, variant.price);
          result.numerical.price.max = Math.max(result.numerical.price.max, variant.price);
        }
      });
    }
  });

  // Convert Sets to Arrays for easier use
  result.unique.seriesName = Array.from(result.unique.seriesName);
  result.unique.setName = Array.from(result.unique.setName);
  result.unique.types = Array.from(result.unique.types);
  result.unique.rarity = Array.from(result.unique.rarity);
  
  // Filter out "Unknown Set" and empty set names
  result.unique.setName = result.unique.setName.filter(setName => 
    setName && setName.toLowerCase() !== 'unknown set' && setName.trim() !== ''
  );
  
  // Reset values if no valid numbers were found
  if (result.numerical.hp.min === Infinity) result.numerical.hp = { min: null, max: null };
  if (result.numerical.price.min === Infinity) result.numerical.price = { min: null, max: null };
  
  return result;
}

// WO-10: queries that name a sealed product type show sealed products above the card results (SEALED_QUERY, now in
// lib/productTypes). WO-20: All / Cards / Sealed tabs (?kind=), sealed kind filter (?ptype=, since ?type= is the
// Pokemon energy type filter) and the game (?game=, only offered once a second game is active).
const SEALED_PAGE = 40;
const STRIP_SIZE = 8;

// Columns of the card grid below (grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4), so the sealed strip can sit
// right after the first row of cards.
function useCardColumns() {
  const cols = () => {
    const w = typeof window !== 'undefined' ? window.innerWidth : 1280;
    return w >= 1280 ? 4 : w >= 768 ? 3 : w >= 640 ? 2 : 1;
  };
  const [n, setN] = useState(cols);
  useEffect(() => {
    const onResize = () => setN(cols());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return n;
}
const KEEP_PARAMS = ['kind', 'ptype', 'game'];

const Search = () => {
  const { query } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { currency, convertCadAmount, convertToCadAmount } = useCurrency();
  const title = query ? `Search: ${query} | Lilycove` : 'Search | Lilycove';
  // WO-26: no ?game= searches every active game (tcg=all); a picked game searches only that game, as before.
  const { game, games, setGame } = useSearchGame();
  const tcgParam = searchTcgParam(game);
  const kind = ['cards', 'sealed'].includes(searchParams.get('kind')) ? searchParams.get('kind') : 'all';
  const ptype = searchParams.get('ptype') || 'all';
  const ptypeGroup = ptype === 'all' ? null : groupByKey(ptype);
  const ptypeTypes = ptypeGroup ? ptypeGroup.types.join(',') : '';
  const setKeptParam = (key, value, dflt) => {
    const p = new URLSearchParams(window.location.search);
    if (!value || value === dflt) p.delete(key); else p.set(key, value);
    if (key === 'kind' && value !== 'sealed') p.delete('ptype');
    setSearchParams(p, { replace: true });
  };
  const [sealed, setSealed] = useState({ key: '', items: [], total: 0, types: [], games: [], status: 'idle' });
  // WO-26: card hits per game of the last tcg=all answer ([{game_id, total}]), for the game tab counts.
  const [cardGames, setCardGames] = useState([]);
  // Card search finished for this query (isLoading starts false, so it alone cannot tell "no cards" from "not yet").
  const [cardsDoneFor, setCardsDoneFor] = useState(null);
  const cardColumns = useCardColumns();
  
  // Main states
  const [searchResults, setSearchResults] = useState([]);
  const [searchWithinResults, setSearchWithinResults] = useState('');
  const [sortOption, setSortOption] = useState('relevance');
  
  // Pagination states for infinite scroll
  const [currentPage, setCurrentPage] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [hasMoreResults, setHasMoreResults] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  
  // Filter data and states
  const [filterData, setFilterData] = useState({
    unique: {
      seriesName: [],
      setName: [],
      types: [],
      rarity: []
    },
    numerical: {
      hp: { min: null, max: null },
      price: { min: null, max: null }
    }
  });
  
  // Selected filter states
  const [selectedSets, setSelectedSets] = useState([]);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [selectedRarities, setSelectedRarities] = useState([]);
  const [priceRange, setPriceRange] = useState({ min: null, max: null });
  const [hpRange, setHpRange] = useState({ min: null, max: null });
  
  // UI states
  const [filterOpen, setFilterOpen] = useState({
    sets: false,
    types: false,
    rarity: false,
    price: false,
    hp: false
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Batch pricing state
  const [bulkPricingData, setBulkPricingData] = useState({});
  const [pricingLoading, setPricingLoading] = useState(false);
  
  // Batch collection/wishlist status to prevent N+1 queries
  const [collectionStatus, setCollectionStatus] = useState({}); // { cardId: { inCollection: bool, inWishlist: bool } }
  
  const apiUrl = process.env.REACT_APP_API_URL || '';
  // WO-26: every new search (query or game) bumps this; answers of an older search are dropped, so switching game
  // tabs quickly can no longer leave the previous game's cards (or prices) on screen.
  const searchSeq = useRef(0);

  // Fetch bulk pricing data for all cards
  const fetchBulkPricing = useCallback(async (cards, seq = searchSeq.current) => {
    if (!cards || cards.length === 0) return;
    
    setPricingLoading(true);
    
    try {
      const cardIds = cards.map(card => card.id || card.card_id).filter(Boolean);
      
      if (cardIds.length === 0) return;
      
      // Use POST endpoint for bulk pricing
      const response = await fetch(`${apiUrl}/api/cards/bulk-pricing`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(cardIds),
      });
      
      if (response.ok) {
        const result = await response.json();
        if (seq !== searchSeq.current) return;
        if (result.success && result.data) {
          setBulkPricingData(result.data);
          
          // Now that we have pricing data, calculate and set price range
          // (WO-26: plus other games' TCGplayer prices, which bulk pricing does not know)
          const prices = Object.values(result.data)
            .filter(pricing => pricing && pricing.price && pricing.price > 0)
            .map(pricing => pricing.price)
            .concat(cards.map(card => card.primary_price?.price).filter(price => price > 0));
          
          if (prices.length > 0) {
            const minPrice = Math.min(...prices);
            const maxPrice = Math.max(...prices);
            setPriceRange({
              min: minPrice,
              max: maxPrice
            });
            // the data's own range: the "(Filtered)" label and Reset compare against it
            setFilterData(prev => ({ ...prev, numerical: { ...prev.numerical, price: { min: minPrice, max: maxPrice } } }));
          }
        }
      }
    } catch (error) {
      console.error('Error fetching bulk pricing:', error);
      // Don't set error state - allow cards to fall back to individual pricing
    } finally {
      if (seq === searchSeq.current) setPricingLoading(false);   // a newer search's pricing may still be loading
    }
  }, [apiUrl]);
  
  // Memoize setCounts calculation to prevent recalculation on every render
  const setCounts = useMemo(() => {
    const counts = {};
    searchResults.forEach(card => {
      if (card.setName && card.setName !== 'Unknown Set') {
        const setNameKey = card.setName.toLowerCase();
        counts[setNameKey] = (counts[setNameKey] || 0) + 1;
      }
    });
    return counts;
  }, [searchResults]);

  // Type color mapping
  const typeColors = {
    'darkness': 'bg-purple',
    'fire': 'bg-red-600',
    'water': 'bg-skyblue',
    'grass': 'bg-green',
    'electric': 'bg-cream',
    'fighting': 'bg-red-800',
    'psychic': 'bg-pink-500',
    'metal': 'bg-gray-400',
    'colorless': 'bg-gray-200',
    'dragon': 'bg-orange-500',
    'fairy': 'bg-pink-300'
  };
  // Fetch search results based on query parameter
  useEffect(() => {
    // Skip the API call if query is empty
    if (!query) {
      setSearchResults([]);
      setCurrentPage(1);
      setTotalResults(0);
      setHasMoreResults(true);
      return;
    }
    
    const seq = ++searchSeq.current;
    const fetchInitialData = async () => {
      const searchStartedAt = Date.now();
      setIsLoading(true);
      setError(null);
      setCurrentPage(1);
      setSearchResults([]);
      setCardGames([]);
      setPriceRange({ min: null, max: null });   // WO-26: a new search (or game) starts with no price range
      
      try {
        const data = await fetchSearchJson(`${apiUrl}/api/search/?query=${encodeURIComponent(query)}&page=1${tcgParam}`);
        if (seq !== searchSeq.current) return;   // a newer search (other game / query) owns the page now
        setCardGames(Array.isArray(data.games) ? data.games : []);

        // Extract the results array from the API response
        let cards = data.results || data;
        
        // Deduplicate cards by ID - variants shouldn't count as separate cards
        const seenCardIds = new Set();
        const uniqueCards = [];
        cards.forEach(card => {
          const cardId = card.id || card.card_id;
          if (cardId && !seenCardIds.has(cardId)) {
            seenCardIds.add(cardId);
            uniqueCards.push(card);
          }
        });
        cards = uniqueCards;
        
        // Track searches for both zero-result and non-zero-result outcomes.
        analyticsService.trackSearch(query, cards.length, {
          page: 1,
          results_count: cards.length,
          search_time_ms: Date.now() - searchStartedAt,
          total_results: data.total || cards.length,
          zero_result: cards.length === 0
        }).catch(err => console.warn('Analytics tracking failed:', err));
        
        // Set pagination info - use total from API (true total in database)
        setTotalResults(data.total || cards.length);
        // Use has_more from API response for accurate pagination
        setHasMoreResults(data.has_more === true);

        // Analyze card data to extract filter options - we'll need to fetch more data for complete filters
        const uniqueValues = analyzeCardData(cards);
        
        setFilterData(uniqueValues);
        setSearchResults(cards);
        
        // Don't auto-initialize HP range - let users set it explicitly if they want
        // Auto-initializing would filter out cards without HP values unintentionally
        // HP range should only be set from URL parameters or user interaction
        
        // Don't initialize price range from data - wait for bulk pricing to load
        // Price range will be set after bulk pricing completes
        
        // Fetch batch pricing for all cards
        if (cards.length > 0) {
          fetchBulkPricing(cards, seq);
          fetchBatchCollectionStatus(cards);
        }
      } catch (err) {
        if (seq !== searchSeq.current) return;
        setError(err.message);
        console.error('Error fetching search results:', err);
      } finally {
        if (seq === searchSeq.current) {
          setIsLoading(false);
          setCardsDoneFor(query);
        }
      }
    };
      fetchInitialData();
    // fetchBatchCollectionStatus is intentionally excluded to avoid rerunning
    // the full search effect on auth-related re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, apiUrl, fetchBulkPricing, tcgParam]);

  // WO-20: sealed products for this query (All tab: the top few; Sealed tab: paged, filtered by kind).
  const sealedKey = `${query}|${tcgParam}|${kind === 'sealed' ? ptypeTypes : ''}`;
  useEffect(() => {
    if (!query || query.trim().length < 2) { setSealed({ key: sealedKey, items: [], total: 0, types: [], status: 'ready' }); return undefined; }
    const ctrl = new AbortController();
    setSealed((prev) => ({ ...prev, key: sealedKey, items: [], status: 'loading' }));
    const typeQ = kind === 'sealed' && ptypeTypes ? `&product_type=${encodeURIComponent(ptypeTypes)}` : '';
    fetchSearchJson(`${apiUrl}/api/search/products?query=${encodeURIComponent(query)}&limit=${SEALED_PAGE}${typeQ}${tcgParam}`, { signal: ctrl.signal })
      .catch((e) => { if (e.name === 'AbortError') throw e; return { results: [] }; })
      .then((data) => setSealed({
        key: sealedKey,
        items: Array.isArray(data.results) ? data.results : [],
        total: Number.isFinite(data.total) ? data.total : (data.results || []).length,
        types: Array.isArray(data.types) ? data.types : [],
        games: Array.isArray(data.games) ? data.games : [],
        status: 'ready',
      }))
      .catch((e) => { if (e.name !== 'AbortError') setSealed({ key: sealedKey, items: [], total: 0, types: [], games: [], status: 'ready' }); });
    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl, sealedKey]);

  const loadMoreSealed = () => {
    const typeQ = ptypeTypes ? `&product_type=${encodeURIComponent(ptypeTypes)}` : '';
    fetchSearchJson(`${apiUrl}/api/search/products?query=${encodeURIComponent(query)}&limit=${SEALED_PAGE}&offset=${sealed.items.length}${typeQ}${tcgParam}`)
      .then((data) => setSealed((prev) => (prev.key !== sealedKey ? prev : {
        ...prev, items: [...prev.items, ...(Array.isArray(data.results) ? data.results : [])],
      })))
      .catch(() => {});
  };

  // Batch fetch collection/wishlist status for all cards to prevent N+1 queries
  const fetchBatchCollectionStatus = useCallback(async (cards) => {
    if (!user || !cards || cards.length === 0) return;
    
    try {
      // Fetch user's collections and wishlist once
      const [collectionResponse, wishlistResponse] = await Promise.all([
        collectionAPI.getUserCollections(user.id),
        wishlistAPI.getUserWishlist(user.id)
      ]);
      
      // Build status map for all cards
      const statusMap = {};
      const cardIds = new Set(cards.map(card => card.id || card.card_id).filter(Boolean));
      
      // Check collections
      if (collectionResponse.data) {
        collectionResponse.data.forEach(collection => {
          if (collection.items) {
            collection.items.forEach(item => {
              if (cardIds.has(item.card_id)) {
                if (!statusMap[item.card_id]) {
                  statusMap[item.card_id] = {};
                }
                statusMap[item.card_id].inCollection = true;
              }
            });
          }
        });
      }
      
      // Check wishlist
      if (wishlistResponse.data) {
        wishlistResponse.data.forEach(item => {
          if (cardIds.has(item.card_id)) {
            if (!statusMap[item.card_id]) {
              statusMap[item.card_id] = {};
            }
            statusMap[item.card_id].inWishlist = true;
          }
        });
      }
      
      setCollectionStatus(statusMap);
    } catch (error) {
      // Silently fail - don't block UI if status fetch fails
      console.warn('Failed to fetch collection/wishlist status:', error);
    }
  }, [user]);
  
  // Function to load more results
  const loadMoreResults = useCallback(async () => {
    if (isLoadingMore || !hasMoreResults) return;
    
    setIsLoadingMore(true);
    const nextPage = currentPage + 1;
    const seq = searchSeq.current;
    
    try {
      const data = await fetchSearchJson(`${apiUrl}/api/search/?query=${encodeURIComponent(query)}&page=${nextPage}${tcgParam}`);
      if (seq !== searchSeq.current) return;   // the game or query changed while this page loaded
      let newCards = data.results || [];
      
      // Deduplicate new cards by ID
      const existingCardIds = new Set(searchResults.map(card => card.id || card.card_id).filter(Boolean));
      newCards = newCards.filter(card => {
        const cardId = card.id || card.card_id;
        return cardId && !existingCardIds.has(cardId);
      });
      
      if (newCards.length > 0) {
        // Append new unique cards to existing results
        const updatedCards = [...searchResults, ...newCards];
        setSearchResults(updatedCards);
        setCurrentPage(nextPage);
        
        // Update filter data with all cards
        const updatedUniqueValues = analyzeCardData(updatedCards);
        setFilterData(updatedUniqueValues);
        
        // Update price and HP ranges if needed
        if (updatedUniqueValues.numerical.price.min !== null) {
          setPriceRange(prev => ({
            min: Math.min(prev.min || updatedUniqueValues.numerical.price.min, updatedUniqueValues.numerical.price.min),
            max: Math.max(prev.max || updatedUniqueValues.numerical.price.max, updatedUniqueValues.numerical.price.max)
          }));
        }
        
        if (updatedUniqueValues.numerical.hp.min !== null) {
          setHpRange(prev => ({
            min: Math.min(prev.min || updatedUniqueValues.numerical.hp.min, updatedUniqueValues.numerical.hp.min),
            max: Math.max(prev.max || updatedUniqueValues.numerical.hp.max, updatedUniqueValues.numerical.hp.max)
          }));
        }
        
        // Fetch bulk pricing for new cards
        if (newCards.length > 0) {
          fetchBulkPricing(updatedCards, seq);
        }
      }
      
      // Check if there are more results
      const totalLoaded = searchResults.length + newCards.length;
      setHasMoreResults(totalLoaded < (data.total || totalLoaded));
      
    } catch (err) {
      console.error('Error loading more results:', err);
    } finally {
      setIsLoadingMore(false);
    }
  }, [isLoadingMore, hasMoreResults, currentPage, apiUrl, query, searchResults, fetchBulkPricing, tcgParam]);

  // Infinite scroll effect (card results; the Sealed tab pages with its own button)
  useEffect(() => {
    if (kind === 'sealed') return undefined;
    const handleScroll = () => {
      if (
        window.innerHeight + document.documentElement.scrollTop
        >= document.documentElement.offsetHeight - 1000 // Load more when 1000px from bottom
      ) {
        loadMoreResults();
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [loadMoreResults, kind]);
  // Load filters from URL parameters on initial load
  useEffect(() => {
    // Parse URL parameters and set initial filter state
    // Normalize all filter values to lowercase to match filterData format
    const setParams = searchParams.getAll('set').map(s => s.toLowerCase().trim());
    const typeParams = searchParams.getAll('type').map(t => t.toLowerCase().trim());
    const rarityParams = searchParams.getAll('rarity').map(r => r.toLowerCase().trim());
    const minPrice = searchParams.get('minPrice');
    const maxPrice = searchParams.get('maxPrice');
    const minHp = searchParams.get('minHp');
    const maxHp = searchParams.get('maxHp');
    const search = searchParams.get('search');
    const sort = searchParams.get('sort');
    
    if (setParams.length > 0) setSelectedSets(setParams);
    if (typeParams.length > 0) setSelectedTypes(typeParams);
    if (rarityParams.length > 0) setSelectedRarities(rarityParams);
    if (search) setSearchWithinResults(search);
    if (sort) setSortOption(sort);
    
    if (minPrice || maxPrice) {
      setPriceRange({
        min: minPrice ? parseFloat(minPrice) : null,
        max: maxPrice ? parseFloat(maxPrice) : null
      });
    }
    
    if (minHp || maxHp) {
      setHpRange({
        min: minHp ? parseInt(minHp, 10) : null,
        max: maxHp ? parseInt(maxHp, 10) : null
      });
    }
  }, [searchParams]);

  // Update URL parameters when filters change
  const updateUrlParams = () => {
    const params = new URLSearchParams();
    
    // Add all selected sets
    selectedSets.forEach(set => params.append('set', set));
    
    // Add all selected types
    selectedTypes.forEach(type => params.append('type', type));
    
    // Add all selected rarities
    selectedRarities.forEach(rarity => params.append('rarity', rarity));
    
    // Add price range if set
    if (priceRange.min !== null) params.set('minPrice', priceRange.min);
    if (priceRange.max !== null) params.set('maxPrice', priceRange.max);
    
    // Add HP range if set
    if (hpRange.min !== null) params.set('minHp', hpRange.min);
    if (hpRange.max !== null) params.set('maxHp', hpRange.max);
    
    // Add search within results
    if (searchWithinResults) params.set('search', searchWithinResults);
    
    // Add sort option
    if (sortOption !== 'relevance') params.set('sort', sortOption);
    
    // WO-20: keep the tab / sealed kind / game params (they are not filter state)
    const current = new URLSearchParams(window.location.search);
    KEEP_PARAMS.forEach((k) => { if (current.get(k)) params.set(k, current.get(k)); });

    // Update URL without reloading the page
    setSearchParams(params);
  };  // Debounced version of updateUrlParams
  const debouncedUpdateUrlParams = useMemo(
    () => debounce(updateUrlParams, 500),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Update URL when filters change
  useEffect(() => {
    debouncedUpdateUrlParams();
    // Clean up on unmount
    return () => debouncedUpdateUrlParams.cancel();
  }, [selectedSets, selectedTypes, selectedRarities, priceRange, hpRange, searchWithinResults, sortOption, debouncedUpdateUrlParams]);

  // Memoized helper function to get lowest price - prevents recalculation
  const getLowestPrice = useCallback((card) => {
    const cardId = card.id || card.card_id;
    
    // 1. Check bulk pricing data first (most up-to-date)
    if (bulkPricingData && cardId && bulkPricingData[cardId]) {
      const pricing = bulkPricingData[cardId];
      if (pricing.price && pricing.price > 0) {
        return pricing.price;
      }
    }
    
    // 2. Try primaryPrice from card data
    if (card.primaryPrice && card.primaryPrice > 0) {
      return card.primaryPrice;
    }
    // WO-26: other games' cards carry their TCGplayer price
    if (card.primary_price?.price > 0) {
      return card.primary_price.price;
    }
    
    // 3. Fallback to static variants calculation
    if (!card || !card.variants || Object.keys(card.variants).length === 0) return Infinity;
    
    const prices = Object.values(card.variants)
      .filter(v => v && v.price !== undefined && v.price > 0)
      .map(v => v.price);
      
    return prices.length > 0 ? Math.min(...prices) : Infinity;
  }, [bulkPricingData]);
  
  // Apply filters to search results - optimized with useMemo to prevent blocking
  const filteredResults = useMemo(() => {
    if (!searchResults.length) return [];
    
    let filtered = [...searchResults];
    
    // Filter by search within results (card name)
    if (searchWithinResults) {
      const searchLower = searchWithinResults.toLowerCase();
      filtered = filtered.filter(card => 
        card.name && card.name.toLowerCase().includes(searchLower)
      );
    }
    
    // Filter by selected sets
    if (selectedSets.length > 0) {
      // Normalize selectedSets to ensure all are lowercase (defensive)
      const normalizedSelectedSets = selectedSets.map(s => s.toLowerCase().trim());
      
      filtered = filtered.filter(card => {
        // Cards must have setName to match set filters
        if (!card.setName) {
          return false;
        }
        
        // Normalize the card's set name to lowercase for comparison
        const cardSetNameLower = card.setName.toLowerCase().trim();
        
        // Check if the card's set name (lowercase) is in the selected sets
        // selectedSets contains lowercase set names from the filter
        const matches = normalizedSelectedSets.includes(cardSetNameLower);
        
        return matches;
      });
    }
    
    // Filter by selected types
    if (selectedTypes.length > 0) {
      filtered = filtered.filter(card => 
        Array.isArray(card.types) && 
        card.types.some(type => type && selectedTypes.includes(type.toLowerCase()))
      );
    }
    
    // Filter by selected rarities
    if (selectedRarities.length > 0) {
      filtered = filtered.filter(card => 
        card.rarity && selectedRarities.includes(card.rarity.toLowerCase())
      );
    }
    
    // Filter by price range
    if (priceRange.min !== null || priceRange.max !== null) {
      filtered = filtered.filter(card => {
        // Try to get price from bulk pricing data first
        const cardId = card.id || card.card_id;
        let cardPrice = null;
        
        // Check bulk pricing data (most up-to-date)
        if (bulkPricingData && cardId && bulkPricingData[cardId]) {
          const pricing = bulkPricingData[cardId];
          if (pricing.price && pricing.price > 0) {
            cardPrice = pricing.price;
          }
        }
        
        // Fallback: Check if card has primary price from new API
        if (!cardPrice && card.primaryPrice && card.primaryPrice > 0) {
          cardPrice = card.primaryPrice;
        }
        // WO-26: other games' cards carry their TCGplayer price
        if (!cardPrice && card.primary_price?.price > 0) {
          cardPrice = card.primary_price.price;
        }
        
        // Fallback: Check static variants for backward compatibility
        if (!cardPrice && card.variants && Object.keys(card.variants).length > 0) {
          const prices = Object.values(card.variants)
            .filter(variant => variant && variant.price !== undefined && variant.price > 0)
            .map(variant => variant.price);
          
          if (prices.length > 0) {
            cardPrice = Math.min(...prices); // Use lowest price for filtering
          }
        }
        
        // If card has no price data yet, don't filter it out - wait for pricing to load
        // Only filter cards that have prices and are out of range
        if (cardPrice === null || cardPrice <= 0) {
          // Don't filter out cards without prices - they might still be loading
          return true;
        }
        
        // Check if price is within the specified range
        const minOk = priceRange.min === null || cardPrice >= priceRange.min;
        const maxOk = priceRange.max === null || cardPrice <= priceRange.max;
        return minOk && maxOk;
      });
    }
    
    // Note: Cards without prices are NOT filtered out - they're kept until pricing loads
    
    // Filter by HP range (only if user explicitly set it, not auto-initialized)
    // Cards without HP should still be shown unless user explicitly filters by HP
    if (hpRange.min !== null || hpRange.max !== null) {
      filtered = filtered.filter(card => {
        // If card has no HP, include it (don't filter out cards without HP)
        if (!card.hp) {
          return true; // Keep cards without HP - they might be Trainer/Energy cards
        }
        
        const hp = parseInt(card.hp, 10);
        if (isNaN(hp)) {
          return true; // Keep cards with invalid HP values
        }
        
        const minOk = hpRange.min === null || hp >= hpRange.min;
        const maxOk = hpRange.max === null || hp <= hpRange.max;
        return minOk && maxOk;
      });
    }
    
    // Apply sorting
    if (sortOption !== 'relevance') {
      filtered.sort((a, b) => {
        switch (sortOption) {
          case 'price-low': {
            // Put $0 cards at the end when sorting low to high
            const priceA = getLowestPrice(a);
            const priceB = getLowestPrice(b);
            if (priceA === 0 && priceB === 0) return 0;
            if (priceA === 0) return 1;  // a goes after b
            if (priceB === 0) return -1; // b goes after a
            return priceA - priceB;
          }
          case 'price-high': {
            // Put $0 cards at the end when sorting high to low too
            const priceA = getLowestPrice(a);
            const priceB = getLowestPrice(b);
            if (priceA === 0 && priceB === 0) return 0;
            if (priceA === 0) return 1;  // a goes after b
            if (priceB === 0) return -1; // b goes after a
            return priceB - priceA;
          }
          case 'name-asc':
            return (a.name || '').localeCompare(b.name || '');
          case 'name-desc':
            return (b.name || '').localeCompare(a.name || '');
          default:
            return 0;
        }
      });
    }
    
    return filtered;
  }, [searchResults, selectedSets, selectedTypes, selectedRarities, priceRange, hpRange, searchWithinResults, sortOption, bulkPricingData, getLowestPrice]);

  // Handle filter reset
  const handleResetFilters = () => {
    setSelectedSets([]);
    setSelectedTypes([]);
    setSelectedRarities([]);
    setPriceRange({ min: filterData.numerical.price.min, max: filterData.numerical.price.max });
    setHpRange({ min: filterData.numerical.hp.min, max: filterData.numerical.hp.max });
    setSearchWithinResults('');
    setSortOption('relevance');
    
    // Clear URL parameters
    navigate(`/search/${query}`);
  };

  // Toggle filter sections
  const toggleFilter = (section) => {
    setFilterOpen({
      ...filterOpen,
      [section]: !filterOpen[section]
    });
  };

  // Handle sort change
  const handleSortChange = (e) => {
    setSortOption(e.target.value);
  };

  // Handle search within results (debounced)
  const debouncedHandleSearchWithinResults = useMemo(
    () => debounce((value) => {
      setSearchWithinResults(value);
    }, 300),
    []
  );

  useEffect(() => {
    return () => {
      debouncedHandleSearchWithinResults.cancel();
    };
  }, [debouncedHandleSearchWithinResults]);

  // Handle new search submission
  const handleNewSearch = (newQuery) => {
    if (newQuery.trim()) {
      // WO-26: a new query keeps the picked game tab (it used to fall back to Pokemon)
      navigate(`/search/${encodeURIComponent(newQuery.trim())}${game !== ALL_GAMES ? `?game=${encodeURIComponent(game)}` : ''}`);
    }
  };

  // Handle search bar key press
  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleNewSearch(e.target.value);
    }
  };

  const displayPriceBounds = useMemo(() => {
    const minCad = filterData.numerical.price.min;
    const maxCad = filterData.numerical.price.max;
    return {
      min: minCad !== null ? roundPriceAmount(convertCadAmount(minCad, currency), 2) : null,
      max: maxCad !== null ? roundPriceAmount(convertCadAmount(maxCad, currency), 2) : null,
    };
  }, [convertCadAmount, currency, filterData.numerical.price.max, filterData.numerical.price.min]);

  const displayPriceRange = useMemo(() => {
    return {
      min: priceRange.min !== null ? roundPriceAmount(convertCadAmount(priceRange.min, currency), 2) : null,
      max: priceRange.max !== null ? roundPriceAmount(convertCadAmount(priceRange.max, currency), 2) : null,
    };
  }, [convertCadAmount, currency, priceRange.max, priceRange.min]);

  const handleDisplayPriceRangeChange = useCallback((nextDisplayRange) => {
    setPriceRange({
      min: nextDisplayRange.min === null
        ? null
        : roundPriceAmount(convertToCadAmount(nextDisplayRange.min, currency), 2),
      max: nextDisplayRange.max === null
        ? null
        : roundPriceAmount(convertToCadAmount(nextDisplayRange.max, currency), 2),
    });
  }, [convertToCadAmount, currency]);

  const handleSearchResultClick = useCallback((card, position) => {
    analyticsService.trackSearchResultClicked({
      query,
      card_id: card?.id || card?.card_id || null,
      position,
      result_count: filteredResults.length,
      page: currentPage,
      source_page: 'search'
    }).catch(err => console.warn('Analytics tracking failed:', err));
  }, [currentPage, filteredResults.length, query]);

  const cardsSettled = cardsDoneFor === query && !isLoading;
  const noCards = cardsSettled && !error && searchResults.length === 0;
  const sealedFirst = SEALED_QUERY.test(query || '') || noCards;
  const showStrip = kind === 'all' && !sealedFirst && sealed.items.length > 0;
  const sealedAllTo = `/search/${encodeURIComponent(query || '')}?kind=sealed${game !== DEFAULT_GAME && game !== ALL_GAMES ? `&game=${game}` : ''}`;
  // WO-26: in "All games", per-game result counts on the game tabs and a game badge on each result once a result of
  // another game than Pokemon is shown (a Pokemon-only answer looks exactly as before).
  const allGames = game === ALL_GAMES;
  const gameCounts = useMemo(() => {
    if (!allGames) return null;
    const c = {};
    [...cardGames, ...(sealed.games || [])].forEach((g) => { c[g.game_id] = (c[g.game_id] || 0) + (g.total || 0); });
    return c;
  }, [allGames, cardGames, sealed.games]);
  const cardBadges = allGames && showGameBadges(searchResults);
  const sealedBadges = allGames && showGameBadges(sealed.items);

  return (
    <>
      <PageMeta
        title={title}
        description={
          query
            ? `Search results for "${query}" on Lilycove.`
            : 'Search Pokemon cards on Lilycove.'
        }
      />
      {/* Search Bar (Mobile) */}
      <div className="relative mb-8 md:hidden">
        <input 
          type="text" 
          placeholder="Search for cards, sets, tcg..." 
          defaultValue={query}
          onKeyPress={handleSearchKeyPress}
          className="w-full h-9 rounded-md border border-purple/30 bg-dark/80 px-3 py-1 pl-9 text-cream placeholder:text-cream/50"
        />
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="absolute left-2.5 top-2.5 h-5 w-5 text-purple">
          <circle cx="11" cy="11" r="8"></circle>
          <path d="m21 21-4.3-4.3"></path>
        </svg>
      </div>


      {/* Search Results Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>          <h1 className="text-2xl tracking-wider uppercase mb-1 font-medium text-purple">Search Results: {query}</h1>
          <p className="text-cream/70 text-sm tracking-wide">
            {filteredResults.length} of {totalResults > 0 ? totalResults : searchResults.length} Cards
            {sealed.total > 0 && ` · ${sealed.total.toLocaleString()} Sealed`}
            {(selectedSets.length > 0 || selectedTypes.length > 0 || selectedRarities.length > 0 || 
              priceRange.min !== filterData.numerical.price.min || priceRange.max !== filterData.numerical.price.max ||
              hpRange.min !== filterData.numerical.hp.min || hpRange.max !== filterData.numerical.hp.max ||
              searchWithinResults) ? ' (Filtered)' : ''}
          </p>
        </div>

        <div className={`flex flex-col sm:flex-row gap-3 w-full md:w-auto ${kind === 'sealed' ? 'hidden' : ''}`}>
          <div className="relative w-full sm:w-64">
            <input 
              type="text" 
              placeholder="Search within results" 
              defaultValue={searchWithinResults}
              onChange={(e) => debouncedHandleSearchWithinResults(e.target.value)}
              className="w-full h-9 rounded-md border border-purple/30 bg-dark/80 px-3 py-1 pl-9 text-cream placeholder:text-cream/50"
            />
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="absolute left-2.5 top-2.5 h-4 w-4 text-purple/70">
              <circle cx="11" cy="11" r="8"></circle>
              <path d="m21 21-4.3-4.3"></path>
            </svg>
          </div>

          <div className="flex gap-3">
            <div className="relative inline-block w-[140px]">
              <select 
                id="sort-select" 
                value={sortOption}
                onChange={handleSortChange}
                className="w-full h-9 rounded-md border border-purple/30 bg-dark px-3 py-1 text-cream appearance-none pr-8"
              >
                <option value="relevance">Relevance</option>
                <option value="price-low">Price: Low-High</option>
                <option value="price-high">Price: High-Low</option>
                <option value="name-asc">Name: A-Z</option>
                <option value="name-desc">Name: Z-A</option>
                <option value="newest">Newest First</option>
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-cream">
                  <path d="m6 9 6 6 6-6"/>
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* WO-20: what kind of results (labelled counts), and the game once there is more than one */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-6">
        <PillTabs
          label="Result kind"
          value={kind}
          onChange={(v) => setKeptParam('kind', v, 'all')}
          options={[
            { value: 'all', label: 'All' },
            { value: 'cards', label: 'Cards', count: totalResults > 0 ? totalResults : searchResults.length },
            { value: 'sealed', label: 'Sealed', count: sealed.total },
          ]}
        />
        <GameTabs game={game} games={games} onChange={setGame} className="sm:ml-auto" allLabel="All games" counts={gameCounts} />
      </div>

      {kind === 'sealed' && (
        <div data-testid="sealed-results">
          <PillTabs
            label="Sealed product type"
            className="mb-6"
            value={ptype}
            onChange={(v) => setKeptParam('ptype', v, 'all')}
            options={[{ value: 'all', label: 'All sealed' },
              ...groupsFromFacets(sealed.types).map((g) => ({ value: g.key, label: g.label, count: g.count }))]}
          />
          {sealed.status === 'loading' && <p className="text-cream/70 text-lg text-center py-10">Loading sealed products...</p>}
          {sealed.status === 'ready' && sealed.items.length === 0 && (
            <p className="text-cream/70 text-lg text-center py-10">No sealed products found for "{query}"</p>
          )}
          {sealed.items.length > 0 && <ProductGrid items={sealed.items} showGame={sealedBadges} />}
          {sealed.status === 'ready' && sealed.items.length > 0 && sealed.items.length < sealed.total && (
            <div className="flex justify-center mt-8">
              <button type="button" onClick={loadMoreSealed}
                      className="border border-purple/40 text-cream hover:bg-purple/20 tracking-wider uppercase text-sm py-2 px-6 rounded-md">
                Load more
              </button>
            </div>
          )}
        </div>
      )}

      {/* All tab: sealed first only for sealed-intent queries ("elite trainer box", "booster box", "tin") or when no card
          matches. Card queries ("charizard") keep cards first; sealed is a one-row strip after the first card row. */}
      {kind === 'all' && sealed.items.length > 0 && sealedFirst && (
        <SealedProductsSection
          items={sealed.items.slice(0, 10)}
          showGame={sealedBadges}
          title="Sealed products"
          flush
          total={sealed.total}
          seeAllTo={sealedAllTo}
          seeAllLabel={`See all ${sealed.total.toLocaleString()} sealed`}
        />
      )}
      {kind === 'all' && sealed.items.length > 0 && sealedFirst && !noCards && (
        <h2 className="text-xl tracking-wider uppercase mb-4 font-medium text-purple mt-10">Cards</h2>
      )}
      {/* Only sealed matched: one line instead of an empty card grid with its filters */}
      {kind === 'all' && sealed.items.length > 0 && noCards && (
        <p className="text-cream/70 mb-8">No cards found for "{query}".</p>
      )}

      {/* Content Grid with Sidebar */}
      <div className={`grid grid-cols-1 lg:grid-cols-4 gap-6 ${kind === 'sealed' || (kind === 'all' && sealed.items.length > 0 && noCards) ? 'hidden' : ''}`}>
        {/* Filters Sidebar */}
        <div className="hidden lg:block" id="search-filters">
          <div className="bg-dark border border-purple/20 rounded-lg p-4">
            <h2 className="text-lg tracking-wider uppercase mb-6 text-purple font-medium">Collection Filters</h2>

            {/* Sets Filter */}
            <CategoryFilter 
              title="Sets"
              isOpen={filterOpen.sets}
              toggleFilter={() => toggleFilter('sets')}
              items={filterData.unique.setName}
              selectedItems={selectedSets}
              onSelectionChange={setSelectedSets}
              showSearch={true}
              itemCounts={setCounts}
            />

            {/* Types Filter */}
            <CategoryFilter 
              title="Types"
              isOpen={filterOpen.types}
              toggleFilter={() => toggleFilter('types')}
              items={filterData.unique.types}
              selectedItems={selectedTypes}
              onSelectionChange={setSelectedTypes}
              colorMap={typeColors}
              showSearch={false}
            />

            {/* Rarity Filter */}
            <CategoryFilter 
              title="Rarity"
              isOpen={filterOpen.rarity}
              toggleFilter={() => toggleFilter('rarity')}
              items={filterData.unique.rarity}
              selectedItems={selectedRarities}
              onSelectionChange={setSelectedRarities}
              showSearch={false}
            />

            {/* Price Range Filter */}
            <RangeFilter
              title="Price Range"
              isOpen={filterOpen.price}
              toggleFilter={() => toggleFilter('price')}
              min={displayPriceBounds.min}
              max={displayPriceBounds.max}
              currentMin={displayPriceRange.min}
              currentMax={displayPriceRange.max}
              onRangeChange={handleDisplayPriceRangeChange}
              unit={currency}
              decimalPlaces={2}
            />

            {/* HP Range Filter */}
            <RangeFilter
              title="HP Range"
              isOpen={filterOpen.hp}
              toggleFilter={() => toggleFilter('hp')}
              min={filterData.numerical.hp.min}
              max={filterData.numerical.hp.max}
              currentMin={hpRange.min}
              currentMax={hpRange.max}
              onRangeChange={setHpRange}
              unit=""
            />

            {/* Action Buttons */}
            <div className="flex gap-2 mt-6">
              <button 
                onClick={handleResetFilters}
                className="w-full border border-purple/30 text-cream/70 hover:text-cream hover:bg-dark/80 tracking-wider uppercase py-2 px-4 rounded-md"
              >
                Reset Filters
              </button>
            </div>
          </div>
        </div>

        {/* Card Grid */}
        <div className="lg:col-span-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4" id="pokemon-card-grid">
            {/* Loading State */}
            {isLoading && (
              <div className="col-span-full text-center py-10">
                <p className="text-cream/70 text-lg">Loading cards...</p>
              </div>
            )}

            {/* Error State */}
            {error && (
              <div className="col-span-full text-center py-10">
                <p className="text-red-500 text-lg">Error: {error}</p>
              </div>
            )}

            {/* Card Items */}
            {!isLoading && !error && filteredResults.map((card, index) => {
              // Stable key - use card.id only, never index fallback
              if (!card.id && !card.card_id) {
                console.warn('Card missing ID:', card);
                return null;
              }
              
              const strip = showStrip && index === Math.min(cardColumns, filteredResults.length) - 1 && (
                <div key="sealed-strip" className="col-span-full min-w-0" data-testid="sealed-strip">
                  <SealedProductsSection
                    items={sealed.items.slice(0, STRIP_SIZE)}
                    showGame={sealedBadges}
                    compact
                    title="Sealed products"
                    total={sealed.total}
                    seeAllTo={sealedAllTo}
                    seeAllLabel={`See all ${sealed.total.toLocaleString()} sealed`}
                  />
                </div>
              );
              return [
                  <CardListItem 
                  key={card.id || card.card_id} 
                  card={card}
                  gameBadge={cardBadges ? (card.tcg || gameOfId(card.id || card.card_id)) : null}
                  bulkPricingData={bulkPricingData}
                  pricingLoading={pricingLoading}
                  setCount={setCounts[card.setName?.toLowerCase()]}
                  collectionStatus={collectionStatus[card.id || card.card_id]}
                  onCardClick={() => handleSearchResultClick(card, index + 1)}
                  navigationContext={{
                    came_from: 'search',
                    search_query: query,
                    search_result_position: index + 1,
                    search_result_count: filteredResults.length
                  }}
                />,
                strip,
              ];
            })}

            {/* No Results State */}
            {!isLoading && !error && searchResults.length > 0 && filteredResults.length === 0 && (
              <div className="col-span-full text-center py-10">
                <p className="text-cream/70 text-lg">No cards match the selected filters</p>
                <button 
                  onClick={handleResetFilters}
                  className="mt-3 bg-purple hover:bg-purple/90 text-cream tracking-wider uppercase font-medium py-2 px-4 rounded-md"
                >
                  Reset Filters
                </button>
              </div>
            )}

            {/* No Search Results */}
            {!isLoading && !error && searchResults.length === 0 && (
              <div className="col-span-full text-center py-10">
                <p className="text-cream/70 text-lg">{sealed.items.length > 0 ? `No cards found for "${query}"` : `No results found for "${query}"`}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Search;
